export const MAX_INPUT_BYTES = 20 * 1024 * 1024;
const signatures = { jpeg: [0xff,0xd8,0xff], png: [0x89,0x50,0x4e,0x47,0x0d,0x0a,0x1a,0x0a], webp: [0x52,0x49,0x46,0x46] } as const;
export async function validateImage(file: File): Promise<void> {
  if (file.size > MAX_INPUT_BYTES) throw new Error('La foto supera el límite de 20 MB.');
  if (!['image/jpeg','image/png','image/webp','image/heic','image/heif'].includes(file.type)) throw new Error('Elige una foto JPEG, PNG, WebP o HEIC compatible.');
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  const starts = (sig: readonly number[]) => sig.every((v,i) => bytes[i] === v);
  const isWebp = starts(signatures.webp) && String.fromCharCode(...bytes.slice(8,12)) === 'WEBP';
  if (!starts(signatures.jpeg) && !starts(signatures.png) && !isWebp && !['image/heic','image/heif'].includes(file.type)) throw new Error('El contenido del archivo no corresponde a una imagen válida.');
}
export async function processImage(file: File): Promise<{ display: Blob; thumb: Blob; width: number; height: number; hash: string }> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  if (bitmap.width < 64 || bitmap.height < 64 || bitmap.width * bitmap.height > 80_000_000) { bitmap.close(); throw new Error('Las dimensiones de la foto no son compatibles.'); }
  const render = async (max: number, quality: number) => {
    const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
    const canvas = new OffscreenCanvas(Math.round(bitmap.width * scale), Math.round(bitmap.height * scale));
    canvas.getContext('2d', { alpha: false })!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas.convertToBlob({ type: 'image/webp', quality });
  };
  // iPhone panoramas and Live Photo exports can remain very large even as WebP.
  // Keep compressing until the server's signed-upload limit is guaranteed.
  let display = await render(2560, .82);
  for (const [max, quality] of [[2300,.76],[2000,.70],[1700,.64],[1450,.58],[1200,.52],[1000,.48]] as const) {
    if (display.size <= 1_700_000) break;
    display = await render(max, quality);
  }
  if (display.size > 1_800_000) { bitmap.close(); throw new Error('No hemos podido comprimir esta foto lo suficiente. Prueba con otra imagen.'); }
  let thumb = await render(560, .72);
  if (thumb.size > 170_000) thumb = await render(420, .62);
  const bytes = new Uint8Array(await display.arrayBuffer());
  const hash = globalThis.crypto?.subtle
    ? [...new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes))].map(v => v.toString(16).padStart(2,'0')).join('')
    : demoHash(bytes);
  const width = bitmap.width, height = bitmap.height; bitmap.close();
  return { display, thumb, width, height, hash };
}

/** Allows local HTTP device testing only. Production requires HTTPS and SHA-256. */
function demoHash(bytes: Uint8Array): string {
  let hash = 0x811c9dc5;
  for (const byte of bytes) { hash ^= byte; hash = Math.imul(hash, 0x01000193); }
  return `demo-${(hash >>> 0).toString(16).padStart(8, '0')}-${bytes.length}`;
}
